import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BulkSelectBenefitsEmployeeDialogComponent } from '@app/components';
import { BenefitPlanEmployeeAssignmentService } from '@app/modules/benefits/services';
import { BenefitsEnrollmentSettings } from '@app/modules/forms/benefits/components';
import { NotifyService } from '@app/services/notify.service';
import { BenefitsResolver } from '@benefits/benefits.resolver';
import { BaseForm } from '@forms/base.form';
import { BenefitPlan } from '@models/benefits/benefit-plan.model';
import { Employee } from '@models/employee/employee.model';

@Component({
    templateUrl: './employee-store.template.html',
})
export class EmployeeStoreView extends FormData implements OnInit {
    @ViewChild('selectEmployees', { static: true }) selectEmployees: BulkSelectBenefitsEmployeeDialogComponent;
    @ViewChild('BenefitsEnrollmentSettings')
    BenefitsEnrollmentSettings: BenefitsEnrollmentSettings;

    benefitPlan: BenefitPlan;
    isLoading = false;
    title = 'Assign employees to Benefits';
    attach: number[];
    detach: number[];
    selectedEmployees: Employee[];
    traditionalBenefits = false;

    constructor(
        private route: ActivatedRoute,
        private notify: NotifyService,
        private router: Router,
        private resolver: BenefitsResolver,
        private benefitPlanEmployeeAssignmentService: BenefitPlanEmployeeAssignmentService
    ) {
        super();
        this.benefitPlan = this.route.snapshot.data.models.get('benefitPlan');
    }

    ngOnInit(): void {
        this.isLoading = false;
        if (!this.benefitPlan?.benefitPolicy?.hasEnhancedManagedBenefits) {
            this.traditionalBenefits = true;
        }
    }

    submitAndValidate(form: BaseForm): boolean {
        if (form) {
            form?.submit();
            return form.valid;
        }
        return true;
    }

    onAddToPlan(): void {
        if (
            this.benefitPlan?.benefitPolicy?.hasEnhancedManagedBenefits &&
            !this.submitAndValidate(this.BenefitsEnrollmentSettings)
        ) {
            return;
        }

        if (!this.attach && !this.detach) {
            this.router.navigate(['../../'], { relativeTo: this.route });
            return;
        }

        this.isLoading = true;

        this.benefitPlanEmployeeAssignmentService
            .onSave(
                this.benefitPlan,
                this.attach,
                this.detach,
                this.BenefitsEnrollmentSettings?.selectedBenefitDivision?.id,
                this.BenefitsEnrollmentSettings?.waiveWaitingPeriod
            )
            .then(() => {
                this.resolve();
                this.notify.success('Benefit plan saved');
                this.isLoading = false;
                this.router.navigate(['../../'], { relativeTo: this.route });
            })
            .catch((err) => {
                this.notify.error(err.message || 'Unable to save benefits plan');
                this.isLoading = false;
            });
    }

    resolve(): void {
        this.isLoading = true;
        this.resolver.resolve(this.route.snapshot, this.router.routerState.snapshot).then((models) => {
            this.benefitPlan = <BenefitPlan>models.get('benefitPlan');

            this.isLoading = false;
        });
    }

    activeEmployeesOnBenefitPlan() {
        return this.benefitPlan.employees.filter((employee) => employee.status !== 'terminated');
    }

    onCancel(): void {
        this.router.navigate(['../../'], { relativeTo: this.route });
    }

    onAssign(): void {
        // Clone existing set as we dont want to pass reference
        this.selectEmployees.show<Employee[]>([...this.activeEmployeesOnBenefitPlan()]).then((selected: Employee[]) => {
            this.selectedEmployees = selected;
            const attach = selected
                .filter((e: Employee) => {
                    return !this.benefitPlan.employees.includes(e);
                })
                .map((e: Employee) => e.id);

            const detach = this.activeEmployeesOnBenefitPlan()
                .filter((e: Employee) => {
                    return !selected.includes(e);
                })
                .map((e: Employee) => e.id);

            this.attach = attach;
            this.detach = detach;

            if (this.selectedEmployees) {
                this.selectEmployees.selectItems = this.selectedEmployees;
                this.selectEmployees.selection = this.selectedEmployees;
            }
        });
    }
}
